import React from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { formatCurrency } from '@utils/utils';

// Compact, consistent cells for the device table. Styles: assets/css/modern.css (.cell-*)

export const StatusPill = ({ online }) => (
  <span className={`cell-status ${online ? 'is-online' : 'is-offline'}`}>
    <span className="cell-status__dot" />
    {online ? 'Online' : 'Offline'}
  </span>
);

export const DeviceCell = ({ to, serial, model }) => (
  <Link to={to} className="cell-device">
    <span className="cell-device__serial">{serial}</span>
    {model && <span className="cell-device__model">{model}</span>}
  </Link>
);

export const TextLink = ({ to, children, mono, clamp }) => (
  <Link
    to={to}
    className={`cell-link ${mono ? 'is-mono' : ''} ${clamp ? 'is-clamp' : ''}`}
    title={typeof children === 'string' ? children : undefined}>
    {children}
  </Link>
);

// "Chi nhánh cấp nước Hải Phòng 8" -> "Hải Phòng 8" (full name in tooltip)
export const BranchCell = ({ name }) => {
  if (!name) return <span className="cell-muted">-</span>;
  const short = String(name)
    .replace(/^chi nhánh\s*(cấp nước)?\s*/i, '')
    .trim();
  return (
    <Tooltip title={name}>
      <span className="cell-ellipsis">{short || name}</span>
    </Tooltip>
  );
};

export const DateTimeCell = ({ value }) => {
  if (!value) return <span className="cell-muted">-</span>;
  const d = new Date(Number(value));
  const time = d.toLocaleTimeString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const date = d.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  return (
    <span className="cell-datetime">
      <b>{time}</b>
      <span>{date}</span>
    </span>
  );
};

// RSRP (dBm) as 4 signal bars + value, using the system thresholds
export const SignalCell = ({ rsrp }) => {
  if (rsrp === null || rsrp === undefined || Number.isNaN(rsrp)) {
    return <span className="cell-muted">-</span>;
  }
  const level = rsrp >= -90 ? 4 : rsrp >= -100 ? 3 : rsrp >= -110 ? 2 : 1;
  const tone = rsrp >= -100 ? 'good' : rsrp >= -110 ? 'fair' : 'poor';
  const label = tone === 'good' ? 'Sóng tốt' : tone === 'fair' ? 'Sóng trung bình' : 'Sóng yếu';
  return (
    <Tooltip title={`${label} (${rsrp} dBm)`}>
      <span className={`cell-signal is-${tone}`}>
        <span className="cell-signal__bars" aria-hidden="true">
          {[1, 2, 3, 4].map((i) => (
            <i key={i} className={i <= level ? 'on' : ''} style={{ height: 5 + i * 3 }} />
          ))}
        </span>
        <span className="cell-signal__value">{rsrp}</span>
      </span>
    </Tooltip>
  );
};

export const BatteryCell = ({ percent }) => {
  if (percent === null || percent === undefined) return <span className="cell-muted">-</span>;
  const value = Math.round(percent);
  const tone = value >= 50 ? 'good' : value >= 20 ? 'fair' : 'poor';
  return (
    <span className={`cell-battery is-${tone}`}>
      <span className="cell-battery__body" aria-hidden="true">
        <span style={{ width: `${Math.max(4, value)}%` }} />
      </span>
      <span className="cell-battery__value">{value}%</span>
    </span>
  );
};

export const MeterCell = ({ value }) => (
  <span className="cell-meter">
    {formatCurrency(value, 3)}
    <small>m³</small>
  </span>
);
